import type { EnrichmentPlugin, Event } from '@amplitude/analytics-types';

import { PLATFORMS } from '@/utilities/constants/game';
import { getPlatform } from '@/utilities/getters/gameContextGetters';
import { getDevice } from '@/utilities/logger/amplitude/tracking/helpers';

/**
 * Amplitude reserves `$`-prefixed event types (`$exposure`, `$identify`, ...).
 * Renaming them turns them into plain custom events, so the rename plugins
 * must leave them untouched.
 */
export const isReservedAmplitudeEvent = (eventType: string): boolean =>
  eventType.startsWith('$');

/** Mirrors launcher `changeCasePlugin`. */
export const changeCasePlugin = (): EnrichmentPlugin => ({
  name: 'changeCase',
  execute: async (event: Event) => {
    if (isReservedAmplitudeEvent(event.event_type)) {
      return event;
    }

    const newName = event.event_type
      .replace(/^[-_]*(.)/, (_, c) => c.toUpperCase())
      .replace(/[-_]+(.)/g, (_, c) => ` ${c.toUpperCase()}`);

    return {
      ...event,
      event_type: newName,
    };
  },
});

/** Mirrors launcher `addGLToEventTypePlugin`. */
export const addGLToEventTypePlugin = (): EnrichmentPlugin => ({
  name: 'addGLToEventType',
  execute: async (event: Event) => ({
    ...event,
    event_type:
      !event.event_type.startsWith('GL ') &&
      !isReservedAmplitudeEvent(event.event_type)
        ? `GL ${event.event_type}`
        : event.event_type,
  }),
});

/** Mirrors launcher `addXsellPrefixToEventTypePlugin`. */
export const addXsellPrefixToEventTypePlugin = (): EnrichmentPlugin => ({
  name: 'addXsellPrefixToEventType',
  execute: async (event: Event) => {
    const prefix = 'CAS XS' as const;
    return {
      ...event,
      event_type:
        !event.event_type.startsWith(`${prefix} `) &&
        !isReservedAmplitudeEvent(event.event_type)
          ? `${prefix} ${event.event_type}`
          : event.event_type,
    };
  },
});

/** Mirrors launcher `devicePlugin`. */
export const devicePlugin = (): EnrichmentPlugin => ({
  name: 'addDevice',
  execute: async (event: Event) => ({
    ...event,
    platform: getDevice(),
  }),
});

export const registerAmplitudeEnrichmentPlugins = (
  add: (plugin: EnrichmentPlugin) => unknown,
): void => {
  add(changeCasePlugin());
  add(addGLToEventTypePlugin());
  add(devicePlugin());

  if (getPlatform() === PLATFORMS.XSELL) {
    add(addXsellPrefixToEventTypePlugin());
  }
};
